import {Injectable} from '@angular/core'

import {Observable, BehaviorSubject, map, of, shareReplay} from 'rxjs'

import {LocalStorageService} from '@app/core/services/local-storage.service'
import {Domain, DomainService} from './rest-api'

interface Context {
  id: number
  username: string
  domain: string
  activeDomain: string
  activeTimezone: string
}

const CONTEXT = 'context'

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  refreshTrigger: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)

  private activeDomainSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>(null)
  private activeTimeZoneSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('Browser')

  constructor(
    private store: LocalStorageService,
    private domainService: DomainService,
  ) {}

  get userId(): number {
    return this.getContext().id
  }

  get username(): string {
    return this.getContext().username
  }

  get loginDomain(): string {
    return this.getContext().domain
  }

  get activeDomain(): string {
    return this.getContext().activeDomain
  }

  get activeDomain$(): Observable<string> {
    return this.activeDomainSubject.asObservable()
  }

  get activeTimezone(): string {
    return this.getContext().activeTimezone
  }

  get activeTimezone$(): Observable<string> {
    return this.activeTimeZoneSubject.asObservable()
  }

  private getContext(): Context {
    const context = <Context>this.store.getObject(CONTEXT)
    if (!context) {
      throw new Error('No valid context currently stored')
    }
    // update behavior from a loaded context
    if (this.activeDomainSubject.value !== context.activeDomain) {
      this.activeDomainSubject.next(context.activeDomain)
    }

    // update behavior from a loaded context
    if (this.activeTimeZoneSubject.value !== context.activeTimezone) {
      this.activeTimeZoneSubject.next(context.activeTimezone)
    }

    return context
  }

  private setContext(context: Context) {
    this.store.setObject(CONTEXT, context)
  }

  checkForValidContext() {
    const context = <Context>this.store.getObject(CONTEXT)
    return context
  }

  setAuthenticationContext(id: number, username: string, domain: string) {
    const context: Context = {
      id: id,
      username: username,
      domain: domain,
      activeDomain: domain,
      activeTimezone: 'Browser',
    }
    this.setContext(context)
    this.activeDomainSubject.next(domain)
    this.activeTimeZoneSubject.next(context.activeTimezone)
  }

  changeActiveDomain(domain: string) {
    sessionStorage.setItem('activeDomain', domain)
    const context = this.getContext()
    context.activeDomain = domain
    this.setContext(context)
    this.activeDomainSubject.next(domain)
  }

  changeActiveTimezone(tz: string) {
    const context = this.getContext()
    context.activeTimezone = tz
    this.setContext(context)
    this.activeTimeZoneSubject.next(tz)
  }

  refresh() {
    this.refreshTrigger.next(true)
  }

  clear(): void {
    this.store.removeItem(CONTEXT)
  }

  isCuroUser() {
    if (this.loginDomain === 'CURO' || this.loginDomain === 'PAYTER') {
      return true
    }
    return false
  }

  isCuroUserOrHasDomainOverwriteAccess(): Observable<any> {
    if (this.isCuroUser()) {
      return new Observable((observer) => {
        observer.next(true)
        observer.complete()
      })
    }

    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain: Domain) => {
        return domain.data?.['deactivation_permission'] === 'true' || false
      }),
    )
  }

  allowNewVersionOfSettings(): Observable<boolean> {
    if (this.isCuroUser()) {
      return of(true)
    }

    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain) => {
        if (!domain) return false
        return domain.data?.['terminal_config_v2'] === 'true'
      }),
      shareReplay({bufferSize: 1, refCount: false}), // uhg this fixes a weird emission issue
    )
  }

  allowVisibleTamperState(): Observable<boolean> {
    if (this.isCuroUser()) {
      return of(true)
    }

    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain) => {
        if (!domain) return false
        return domain.data?.['show_tamper_state'] === 'true'
      }),
      shareReplay({bufferSize: 1, refCount: false}), // uhg this fixes a weird emission issue
    )
  }

  allowNewTransactionOverview(): Observable<boolean> {
    if (this.isCuroUser()) {
      return of(true)
    }

    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain) => {
        if (!domain) return false
        return domain.data?.['show_new_transaction_overview'] === 'true'
      }),
      shareReplay({bufferSize: 1, refCount: false}), // uhg this fixes a weird emission issue
    )
  }

  allowShellHub(): Observable<any> {
    if (this.isCuroUser()) {
      return new Observable((observer) => {
        observer.next(true)
        observer.complete()
      })
    }
    return of(false)
  }

  allowExportImportOfAssetsAndTemplates(): Observable<boolean> {
    // 1. Handle the "Curo User" override immediately
    if (this.isCuroUser()) {
      return of(true)
    }

    // 2. Stream the specific property from the FeatureMenu
    // return this.featureService.featuresEnabled$.pipe(
    //   take(1),
    //   map((menu: FeatureMenu) => menu.importExport)
    // );
  }

  allowTamperInformation(): Observable<any> {
    if (this.isCuroUser()) {
      return new Observable((observer) => {
        observer.next(true)
        observer.complete()
      })
    }
    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain: Domain) => {
        return domain.data?.['show_tamper_information'] === 'true' || false
      }),
    )
  }
  // inverse of allow
  blockForcePush(): Observable<any> {
    if (this.isCuroUser()) {
      // not sure we need this but why not
      return new Observable((observer) => {
        observer.next(false)
        observer.complete()
      })
    }
    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain: Domain) => {
        return domain.data?.['allow_force_push'] === 'false' || false
      }),
    )
  }

  allowNotifications(): Observable<any> {
    if (this.isCuroUser()) {
      return new Observable((observer) => {
        observer.next(true)
        observer.complete()
      })
    }
    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain: Domain) => {
        return domain.data?.['notification_permission'] === 'true' || false
      }),
    )
  }

  allowApolloLogDownload(): Observable<any> {
    return this.domainService.getDomain(this.loginDomain).pipe(
      map((domain: Domain) => {
        const permission1 = domain.data?.['log_download_permisson'] === 'true'
        const permission2 = domain.data?.['log_download_permission'] === 'true'
        return permission1 || permission2 || false
      }),
    )
  }
}
